import React, {useState, useRef, useEffect} from "react";
import * as THREE from "three";
import {OBJLoader} from "three/addons/loaders/OBJLoader.js";
import {FaceMesh} from "@mediapipe/face_mesh";
import {Camera} from "@mediapipe/camera_utils";

/**
 * Enhanced Virtual Try-On with Face-Size Compatible Glasses
 */
const SimpleVTO = () => {
	const [loading, setLoading] = useState(false);
	const [modelLoaded, setModelLoaded] = useState(false);
	const [faceDetected, setFaceDetected] = useState(false);
	const [debugInfo, setDebugInfo] = useState([]);
	const [sizeMultiplier, setSizeMultiplier] = useState(1.0);
	// Live manual controls
	const [positionOffsetX, setPositionOffsetX] = useState(0);
	const [positionOffsetY, setPositionOffsetY] = useState(0);
	const [positionOffsetZ, setPositionOffsetZ] = useState(0);
	const [scaleXMultiplier, setScaleXMultiplier] = useState(1.0);
	const [scaleYMultiplier, setScaleYMultiplier] = useState(1.0);
	const [scaleZMultiplier, setScaleZMultiplier] = useState(1.0);

	const videoRef = useRef(null);
	const canvasRef = useRef(null);
	const overlayCanvasRef = useRef(null);
	const sceneRef = useRef(null);
	const rendererRef = useRef(null);
	const cameraRef = useRef(null);
	const glassesRef = useRef(null);
	const faceMeshRef = useRef(null);
	const cameraInstanceRef = useRef(null);

	// Refs to avoid stale closures in MediaPipe callback
	const sizeMultiplierRef = useRef(1.0);
	const positionOffsetRef = useRef({x: 0, y: 0, z: 0});
	const axisScaleMultiplierRef = useRef({x: 1, y: 1, z: 1});

	useEffect(() => {
		sizeMultiplierRef.current = sizeMultiplier;
	}, [sizeMultiplier]);

	useEffect(() => {
		positionOffsetRef.current = {
			x: positionOffsetX,
			y: positionOffsetY,
			z: positionOffsetZ,
		};
	}, [positionOffsetX, positionOffsetY, positionOffsetZ]);

	useEffect(() => {
		axisScaleMultiplierRef.current = {
			x: scaleXMultiplier,
			y: scaleYMultiplier,
			z: scaleZMultiplier,
		};
	}, [scaleXMultiplier, scaleYMultiplier, scaleZMultiplier]);

	const resetPositionOffsets = () => {
		setPositionOffsetX(0);
		setPositionOffsetY(0);
		setPositionOffsetZ(0);
	};

	const resetAxisScaleMultipliers = () => {
		setScaleXMultiplier(1.0);
		setScaleYMultiplier(1.0);
		setScaleZMultiplier(1.0);
	};

	// Enhanced tracking state with face dimensions
	const lastPosition = useRef({
		x: 0,
		y: 0,
		z: 0,
		scale: {x: 1, y: 1, z: 1},
		rotation: {x: 0, y: 0, z: 0},
		faceWidth: 0,
		faceHeight: 0,
	});

	// Smoothing factors
	const smoothingFactors = {
		position: 0.08,
		rotation: 0.15,
		scale: 1,
		dimensions: 0.9, // Smooth face dimension changes
	};

	const addDebugInfo = message => {
		const timestamp = new Date().toLocaleTimeString();
		setDebugInfo(prev => [...prev.slice(-20), {timestamp, message}]);
	};

	// IMPROVED: Enhanced face dimension calculation
	const calculateFaceDimensions = landmarks => {
		try {
			// More accurate face width using multiple reference points
			const leftCheek = landmarks[234]; // Left cheek
			const rightCheek = landmarks[454]; // Right cheek
			const leftTemple = landmarks[127]; // Left temple
			const rightTemple = landmarks[356]; // Right temple

			// More accurate face height using forehead to chin
			const foreheadTop = landmarks[10]; // Top of forehead
			const chinBottom = landmarks[152]; // Bottom of chin
			const noseTip = landmarks[1]; // Nose tip for reference

			let faceWidth = 0;
			let faceHeight = 0;

			// Calculate face width using multiple methods and take average
			const widthMethods = [];

			// Method 1: Cheek to cheek
			if (leftCheek && rightCheek) {
				const cheekWidth = Math.sqrt(
					Math.pow(rightCheek.x - leftCheek.x, 2) +
						Math.pow(rightCheek.y - leftCheek.y, 2)
				);
				widthMethods.push(cheekWidth);
			}

			// Method 2: Temple to temple
			if (leftTemple && rightTemple) {
				const templeWidth = Math.sqrt(
					Math.pow(rightTemple.x - leftTemple.x, 2) +
						Math.pow(rightTemple.y - leftTemple.y, 2)
				);
				widthMethods.push(templeWidth);
			}

			// Use average of available width measurements
			if (widthMethods.length > 0) {
				faceWidth =
					widthMethods.reduce((a, b) => a + b, 0) /
					widthMethods.length;
			}

			// Calculate face height
			if (foreheadTop && chinBottom) {
				faceHeight = Math.sqrt(
					Math.pow(chinBottom.x - foreheadTop.x, 2) +
						Math.pow(chinBottom.y - foreheadTop.y, 2)
				);
			}

			// IMPROVED: Add face shape analysis
			const faceRatio = faceHeight > 0 ? faceWidth / faceHeight : 0;

			return {
				faceWidth,
				faceHeight,
				faceRatio,
				landmarkCount: Object.keys(landmarks).length,
			};
		} catch (error) {
			console.error("Face dimension calculation error:", error);
			return {
				faceWidth: 0,
				faceHeight: 0,
				faceRatio: 0,
				landmarkCount: 0,
			};
		}
	};

	// NEW: Calculate glasses dimensions based on face size
	const calculateGlassesDimensions = (
		faceWidth,
		faceHeight,
		faceRatio,
		ipd
	) => {
		// SMALLER Base dimensions for more realistic glasses
		const BASE_FACE_WIDTH = 1; // Average face width in normalized coordinates
		const BASE_GLASSES_WIDTH = 0.3; // Much smaller base glasses width
		const BASE_GLASSES_HEIGHT = 0.15; // Much smaller base glasses height

		// Calculate width based on face width and IPD (reduced factors)
		let glassesWidth = 0;
		if (faceWidth > 0 && ipd > 0) {
			// Use both face width and IPD for better accuracy (much smaller factors)
			const widthFromFace =
				(faceWidth / BASE_FACE_WIDTH) * BASE_GLASSES_WIDTH;
			const widthFromIPD = ipd * 0.4; // Much smaller IPD-based width
			glassesWidth = (widthFromFace + widthFromIPD) / 2;
		} else if (faceWidth > 0) {
			glassesWidth = (faceWidth / BASE_FACE_WIDTH) * BASE_GLASSES_WIDTH;
		} else {
			glassesWidth = BASE_GLASSES_WIDTH;
		}

		// Calculate height based on face height and ratio (reduced factors)
		let glassesHeight = BASE_GLASSES_HEIGHT;
		if (faceHeight > 0) {
			// Adjust height based on face proportions (smaller adjustments)
			if (faceRatio < 0.7) {
				// Long face - slightly taller glasses
				glassesHeight = BASE_GLASSES_HEIGHT * 1.05;
			} else if (faceRatio > 0.9) {
				// Round face - slightly shorter glasses
				glassesHeight = BASE_GLASSES_HEIGHT * 0.95;
			} else {
				// Average face
				glassesHeight = BASE_GLASSES_HEIGHT;
			}
		}

		// Apply much smaller constraints for realistic glasses sizes
		const minWidth = 0.15;
		const maxWidth = 0.5;
		const minHeight = 0.1;
		const maxHeight = 0.3;

		return {
			width: Math.min(Math.max(glassesWidth, minWidth), maxWidth),
			height: Math.min(Math.max(glassesHeight, minHeight), maxHeight),
			depth: Math.min(Math.max(glassesWidth * 0.2, 0.05), 0.15),
		};
	};

	// ENHANCED: Improved glasses positioning with face-size compatibility
	const calculateGlassesPosition = landmarks => {
		if (!landmarks) return null;

		try {
			// Key landmarks for glasses positioning
			const leftEyeOuter = landmarks[33];
			const rightEyeOuter = landmarks[263];
			const leftEyeInner = landmarks[133];
			const rightEyeInner = landmarks[362];
			const noseBridge = landmarks[168];

			if (
				!leftEyeOuter ||
				!rightEyeOuter ||
				!leftEyeInner ||
				!rightEyeInner ||
				!noseBridge
			) {
				return null;
			}

			// Calculate eye centers
			const leftEyeCenter = {
				x: (leftEyeOuter.x + leftEyeInner.x) / 2,
				y: (leftEyeOuter.y + leftEyeInner.y) / 2,
				z: (leftEyeOuter.z + leftEyeInner.z) / 2,
			};
			const rightEyeCenter = {
				x: (rightEyeOuter.x + rightEyeInner.x) / 2,
				y: (rightEyeOuter.y + rightEyeInner.y) / 2,
				z: (rightEyeOuter.z + rightEyeInner.z) / 2,
			};

			// Center between eyes
			const glassesCenter = {
				x: (leftEyeCenter.x + rightEyeCenter.x) / 2,
				y: (leftEyeCenter.y + rightEyeCenter.y) / 2,
				z: (leftEyeCenter.z + rightEyeCenter.z) / 2,
			};

			// ENHANCED: Calculate face dimensions with improved accuracy
			const faceDimensions = calculateFaceDimensions(landmarks);

			// Calculate IPD (Inter-Pupillary Distance)
			const ipd = Math.sqrt(
				Math.pow(rightEyeCenter.x - leftEyeCenter.x, 2) +
					Math.pow(rightEyeCenter.y - leftEyeCenter.y, 2)
			);

			// NEW: Calculate glasses dimensions based on face size
			const glassesDimensions = calculateGlassesDimensions(
				faceDimensions.faceWidth,
				faceDimensions.faceHeight,
				faceDimensions.faceRatio,
				ipd
			);

			// EXACT POINT POSITIONING: Position glasses exactly on drawn points
			// Use the exact same coordinates as the drawn center point
			const canvasWidth = 640;
			const canvasHeight = 480;

			// Convert the drawn center point coordinates to 3D world coordinates
			// This matches exactly where the red dot is drawn
			const posX = (glassesCenter.x - 0.53) * 2; // Exact center point X
			const posY = -((glassesCenter.y - 0.55) * 2); // Exact center point Y (inverted)
			const posZ = (glassesCenter.z - 0.5) * 0.3; // Slightly closer to face

			// Rotation calculation
			const eyeLineAngle = -Math.atan2(
				rightEyeCenter.y - leftEyeCenter.y,
				rightEyeCenter.x - leftEyeCenter.x
			);

			// DEBUG: Log face and glasses dimensions
			if (Math.random() < 0.01) {
				// Only log occasionally to avoid spam
				addDebugInfo(
					`Face: ${(faceDimensions.faceWidth * 100).toFixed(1)}x${(
						faceDimensions.faceHeight * 100
					).toFixed(1)} | Glasses: ${(
						glassesDimensions.width * 100
					).toFixed(1)}x${(glassesDimensions.height * 100).toFixed(
						1
					)}`
				);
			}

			return {
				x: posX,
				y: posY,
				z: posZ,
				scale: {
					x: glassesDimensions.width * sizeMultiplierRef.current,
					y: glassesDimensions.height * sizeMultiplierRef.current,
					z: glassesDimensions.depth * sizeMultiplierRef.current,
				},
				rotation: {
					x: eyeLineAngle,
					y: eyeLineAngle,
					z: eyeLineAngle,
				},
				faceWidth: faceDimensions.faceWidth,
				faceHeight: faceDimensions.faceHeight,
			};
		} catch (error) {
			addDebugInfo(`Position calculation error: ${error.message}`);
			return null;
		}
	};

	// ENHANCED: Smooth positioning with face dimension smoothing
	const smoothPosition = (newPos, lastPos) => {
		if (!newPos) return lastPos;

		const lastScale =
			typeof lastPos.scale === "number"
				? {x: lastPos.scale, y: lastPos.scale, z: lastPos.scale}
				: lastPos.scale;

		const newScale =
			typeof newPos.scale === "number"
				? {x: newPos.scale, y: newPos.scale, z: newPos.scale}
				: newPos.scale;

		// Smooth face dimensions
		const smoothedFaceWidth =
			lastPos.faceWidth +
			(newPos.faceWidth - lastPos.faceWidth) *
				smoothingFactors.dimensions;
		const smoothedFaceHeight =
			lastPos.faceHeight +
			(newPos.faceHeight - lastPos.faceHeight) *
				smoothingFactors.dimensions;

		return {
			x: lastPos.x + (newPos.x - lastPos.x) * smoothingFactors.position,
			y: lastPos.y + (newPos.y - lastPos.y) * smoothingFactors.position,
			z: lastPos.z + (newPos.z - lastPos.z) * smoothingFactors.position,
			scale: {
				x:
					lastScale.x +
					(newScale.x - lastScale.x) * smoothingFactors.scale,
				y:
					lastScale.y +
					(newScale.y - lastScale.y) * smoothingFactors.scale,
				z:
					lastScale.z +
					(newScale.z - lastScale.z) * smoothingFactors.scale,
			},
			rotation: {
				x:
					lastPos.rotation.x +
					(newPos.rotation.x - lastPos.rotation.x) *
						smoothingFactors.rotation,
				y:
					lastPos.rotation.y +
					(newPos.rotation.y - lastPos.rotation.y) *
						smoothingFactors.rotation,
				z:
					lastPos.rotation.z +
					(newPos.rotation.z - lastPos.rotation.z) *
						smoothingFactors.rotation,
			},
			faceWidth: smoothedFaceWidth,
			faceHeight: smoothedFaceHeight,
		};
	};

	// ENHANCED: Draw face dimensions visualization
	const drawEssentialPoints = (landmarks, canvas) => {
		if (!landmarks || !canvas) return;

		const ctx = canvas.getContext("2d");
		ctx.clearRect(0, 0, canvas.width, canvas.height);

		try {
			const leftEyeOuter = landmarks[33];
			const rightEyeOuter = landmarks[263];
			const leftEyeInner = landmarks[133];
			const rightEyeInner = landmarks[362];
			const noseBridge = landmarks[168];
			const forehead = landmarks[10];
			const chin = landmarks[152];
			const leftCheek = landmarks[234];
			const rightCheek = landmarks[454];

			if (
				leftEyeOuter &&
				rightEyeOuter &&
				leftEyeInner &&
				rightEyeInner &&
				noseBridge
			) {
				// Calculate eye centers
				const leftEyeCenter = {
					x: (leftEyeOuter.x + leftEyeInner.x) / 2,
					y: (leftEyeOuter.y + leftEyeInner.y) / 2,
				};
				const rightEyeCenter = {
					x: (rightEyeOuter.x + rightEyeInner.x) / 2,
					y: (rightEyeOuter.y + rightEyeInner.y) / 2,
				};

				// Calculate center between eyes
				const centerBetweenEyes = {
					x: (leftEyeCenter.x + rightEyeCenter.x) / 2,
					y: (leftEyeCenter.y + rightEyeCenter.y) / 2,
				};

				// Draw face width line if available
				if (leftCheek && rightCheek) {
					ctx.strokeStyle = "#ff4444";
					ctx.lineWidth = 1;
					ctx.setLineDash([5, 5]);
					ctx.beginPath();
					ctx.moveTo(
						leftCheek.x * canvas.width,
						leftCheek.y * canvas.height
					);
					ctx.lineTo(
						rightCheek.x * canvas.width,
						rightCheek.y * canvas.height
					);
					ctx.stroke();
					ctx.setLineDash([]);
				}

				// Draw face height line if available
				if (forehead && chin) {
					ctx.strokeStyle = "#4444ff";
					ctx.lineWidth = 1;
					ctx.setLineDash([5, 5]);
					ctx.beginPath();
					ctx.moveTo(
						forehead.x * canvas.width,
						forehead.y * canvas.height
					);
					ctx.lineTo(chin.x * canvas.width, chin.y * canvas.height);
					ctx.stroke();
					ctx.setLineDash([]);
				}

				// Draw connection line between eyes
				ctx.strokeStyle = "#00ff88";
				ctx.lineWidth = 2;
				ctx.beginPath();
				ctx.moveTo(
					leftEyeCenter.x * canvas.width,
					leftEyeCenter.y * canvas.height
				);
				ctx.lineTo(
					rightEyeCenter.x * canvas.width,
					rightEyeCenter.y * canvas.height
				);
				ctx.stroke();

				// Draw eye centers
				ctx.fillStyle = "#00ff88";
				ctx.beginPath();
				ctx.arc(
					leftEyeCenter.x * canvas.width,
					leftEyeCenter.y * canvas.height,
					3,
					0,
					2 * Math.PI
				);
				ctx.fill();

				ctx.beginPath();
				ctx.arc(
					rightEyeCenter.x * canvas.width,
					rightEyeCenter.y * canvas.height,
					3,
					0,
					2 * Math.PI
				);
				ctx.fill();

				// Draw center point between eyes
				ctx.fillStyle = "#ff4444";
				ctx.beginPath();
				ctx.arc(
					centerBetweenEyes.x * canvas.width,
					centerBetweenEyes.y * canvas.height,
					5,
					0,
					2 * Math.PI
				);
				ctx.fill();

				// Draw nose bridge
				ctx.fillStyle = "#ffaa00";
				ctx.beginPath();
				ctx.arc(
					noseBridge.x * canvas.width,
					noseBridge.y * canvas.height,
					3,
					0,
					2 * Math.PI
				);
				ctx.fill();
			}
		} catch (error) {
			// Silent fail for drawing errors
		}
	};

	// The rest of your existing functions remain the same (initializeThreeJS, loadOBJModel, positionGlassesOnFace, etc.)
	// Initialize Three.js scene
	const initializeThreeJS = () => {
		addDebugInfo("Initializing optimized 3D scene...");

		sceneRef.current = new THREE.Scene();
		sceneRef.current.background = null;

		rendererRef.current = new THREE.WebGLRenderer({
			canvas: overlayCanvasRef.current,
			alpha: true,
			antialias: true,
			preserveDrawingBuffer: true,
		});
		rendererRef.current.setSize(640, 480);
		rendererRef.current.setClearColor(0x000000, 0);

		cameraRef.current = new THREE.PerspectiveCamera(
			60,
			640 / 480,
			0.1,
			500
		);
		cameraRef.current.position.set(0, 0, 0.6);

		// Simple lighting
		const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
		sceneRef.current.add(ambientLight);

		const directionalLight = new THREE.DirectionalLight(0xffffff, 0.9);
		directionalLight.position.set(1, 2, 3);
		sceneRef.current.add(directionalLight);

		addDebugInfo("3D scene ready");
	};

	// Load OBJ model
	const loadOBJModel = async () => {
		setLoading(true);
		addDebugInfo("Loading 3D glasses model...");

		try {
			const loader = new OBJLoader();
			const modelUrl = "/models/glasses-01/oculos.obj";

			const obj = await new Promise((resolve, reject) => {
				loader.load(
					modelUrl,
					obj => {
						addDebugInfo("3D model loaded successfully");
						resolve(obj);
					},
					progress => {
						const percent =
							(progress.loaded / progress.total) * 100;
						if (percent % 25 === 0) {
							addDebugInfo(`Loading: ${percent.toFixed(0)}%`);
						}
					},
					error => {
						addDebugInfo(`Model error: ${error.message}`);
						reject(error);
					}
				);
			});

			// Clear previous model
			if (glassesRef.current) {
				sceneRef.current.remove(glassesRef.current);
			}

			glassesRef.current = obj;
			glassesRef.current.position.set(0, 0, 0);
			glassesRef.current.scale.set(0.01, 0.01, 0.005); // Much smaller initial scale
			glassesRef.current.visible = false;

			// Materials
			glassesRef.current.traverse(child => {
				if (child.isMesh) {
					child.material = new THREE.MeshBasicMaterial({
						color: 0x2a2a2a,
						transparent: true,
						opacity: 0.95,
						side: THREE.DoubleSide,
						depthWrite: false,
						depthTest: false,
					});
				}
			});

			sceneRef.current.add(glassesRef.current);
			setModelLoaded(true);
			addDebugInfo("3D glasses ready for tracking");
		} catch (error) {
			addDebugInfo(`Failed to load model: ${error.message}`);
		} finally {
			setLoading(false);
		}
	};

	// Position glasses on face
	const positionGlassesOnFace = landmarks => {
		if (!glassesRef.current || !landmarks) return;

		try {
			const newPosition = calculateGlassesPosition(landmarks);

			if (!newPosition) {
				glassesRef.current.visible = false;
				return;
			}

			// Apply smoothing
			const smoothedPosition = smoothPosition(
				newPosition,
				lastPosition.current
			);
			lastPosition.current = smoothedPosition;

			// Apply transformations with live offsets and axis multipliers
			glassesRef.current.position.set(
				smoothedPosition.x + positionOffsetRef.current.x,
				smoothedPosition.y + positionOffsetRef.current.y,
				smoothedPosition.z + positionOffsetRef.current.z
			);

			const scale = smoothedPosition.scale;
			let sx, sy, sz;
			if (typeof scale === "object") {
				sx = scale.x * axisScaleMultiplierRef.current.x;
				sy = scale.y * axisScaleMultiplierRef.current.y;
				sz = scale.z * axisScaleMultiplierRef.current.z;
			} else {
				sx = scale * axisScaleMultiplierRef.current.x;
				sy = scale * axisScaleMultiplierRef.current.y;
				sz = scale * axisScaleMultiplierRef.current.z;
			}
			glassesRef.current.scale.set(sx, sy, sz);

			glassesRef.current.rotation.set(
				smoothedPosition.rotation.x,
				smoothedPosition.rotation.y,
				smoothedPosition.rotation.z
			);

			glassesRef.current.visible = true;
		} catch (error) {
			addDebugInfo(`Tracking error: ${error.message}`);
		}
	};

	// Render overlay
	const renderOverlay = () => {
		if (rendererRef.current && sceneRef.current && cameraRef.current) {
			rendererRef.current.render(sceneRef.current, cameraRef.current);
		}
	};

	// Face detection callback
	const onResults = results => {
		if (!canvasRef.current || !videoRef.current) return;

		const canvas = canvasRef.current;
		const ctx = canvas.getContext("2d");
		const videoElement = videoRef.current;

		ctx.clearRect(0, 0, canvas.width, canvas.height);
		ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

		if (
			results.multiFaceLandmarks &&
			results.multiFaceLandmarks.length > 0
		) {
			if (!faceDetected) setFaceDetected(true);

			const landmarks = results.multiFaceLandmarks[0];
			positionGlassesOnFace(landmarks);
			drawEssentialPoints(landmarks, canvas);
		} else {
			if (faceDetected) setFaceDetected(false);
			if (glassesRef.current) {
				glassesRef.current.visible = false;
			}
		}

		renderOverlay();
	};

	// Initialize face detection
	useEffect(() => {
		const initializeFaceDetection = async () => {
			try {
				addDebugInfo("Starting face-size compatible tracking...");

				initializeThreeJS();

				faceMeshRef.current = new FaceMesh({
					locateFile: file => {
						return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
					},
					refineLandmarks: true,
					minDetectionConfidence: 0.5,
					minTrackingConfidence: 0.5,
					staticImageMode: false,
				});

				faceMeshRef.current.onResults(onResults);

				if (videoRef.current) {
					cameraInstanceRef.current = new Camera(videoRef.current, {
						onFrame: async () => {
							if (faceMeshRef.current && videoRef.current) {
								await faceMeshRef.current.send({
									image: videoRef.current,
								});
							}
						},
						width: 640,
						height: 480,
					});

					await cameraInstanceRef.current.start();
					addDebugInfo("Face-size compatible tracking active");

					// Start render loop
					const animate = () => {
						renderOverlay();
						requestAnimationFrame(animate);
					};
					animate();
				}
			} catch (error) {
				addDebugInfo(`Initialization failed: ${error.message}`);
			}
		};

		initializeFaceDetection();

		return () => {
			if (cameraInstanceRef.current) {
				cameraInstanceRef.current.stop();
			}
		};
	}, []);

	return (
		<div style={{padding: "20px", maxWidth: "1200px", margin: "0 auto"}}>
			<h2>Enhanced Virtual Try-On - Face Size Compatible</h2>
			<p>
				Glasses automatically adjust to match your face width and height
			</p>

			<div style={{marginBottom: "20px"}}>
				<button
					onClick={loadOBJModel}
					disabled={loading}
					style={{
						background: loading ? "#6c757d" : "#007bff",
						color: "white",
						border: "none",
						padding: "12px 24px",
						borderRadius: "6px",
						cursor: loading ? "not-allowed" : "pointer",
						fontWeight: "600",
						marginRight: "10px",
					}}
				>
					{loading ? "Loading..." : "Load 3D Glasses"}
				</button>
			</div>

			{/* Manual live adjustments removed here and moved next to video */}

			{/* Enhanced Size Control */}
			<div
				style={{
					background: "#f8f9fa",
					padding: "15px",
					borderRadius: "8px",
					marginBottom: "20px",
				}}
			>
				<h4>Glasses Size Control:</h4>
				<div
					style={{display: "flex", alignItems: "center", gap: "15px"}}
				>
					<label style={{fontWeight: "600"}}>Size Adjustment:</label>
					<input
						type="range"
						min="0.5"
						max="2.0"
						step="0.1"
						value={sizeMultiplier}
						onChange={e =>
							setSizeMultiplier(parseFloat(e.target.value))
						}
						style={{flex: 1}}
					/>
					<span
						style={{
							background: "#007bff",
							color: "white",
							padding: "4px 12px",
							borderRadius: "4px",
							fontWeight: "600",
						}}
					>
						{sizeMultiplier.toFixed(1)}x
					</span>
				</div>
				<div
					style={{marginTop: "8px", fontSize: "12px", color: "#666"}}
				>
					<strong>Tip:</strong> Base size is automatically calculated
					from your face dimensions
				</div>
			</div>

			{/* Live View */}
			<div style={{marginBottom: "20px"}}>
				<h4>Live Preview - Face Size Compatible Glasses</h4>
				<div style={{display: "flex", gap: "16px", alignItems: "flex-start", justifyContent: "center", flexWrap: "wrap"}}>
					<div>
						<div
							style={{
								position: "relative",
								width: "640px",
								height: "480px",
								border: "2px solid #007bff",
								borderRadius: "8px",
								overflow: "hidden",
							}}
						>
							<video
								ref={videoRef}
								style={{
									position: "absolute",
									width: "100%",
									height: "100%",
									objectFit: "cover",
									transform: "scaleX(-1)",
								}}
								playsInline
								muted
								autoPlay
							/>
							<canvas
								ref={canvasRef}
								style={{
									position: "absolute",
									width: "100%",
									height: "100%",
									transform: "scaleX(-1)",
								}}
								width="640"
								height="480"
							/>
							<canvas
								ref={overlayCanvasRef}
								style={{
									position: "absolute",
									width: "100%",
									height: "100%",
									transform: "scaleX(-1)",
								}}
							/>
						</div>
						<div
							style={{
								marginTop: "8px",
								fontSize: "12px",
								color: "#666",
								textAlign: "center",
							}}
						>
							<strong>Visualization:</strong> Red line = Face width | Blue line = Face height | Green line = Eye distance
						</div>
					</div>
					</div>
					<div style={{background: "#f8f9fa", padding: "15px", borderRadius: "8px", width: "360px"}}>
						<h4>Manual Adjustments (live)</h4>
						<div style={{marginBottom: "10px", color: "#666", fontSize: "12px"}}>
							These offsets and axis scales are applied on top of auto-tracking.
						</div>
						<div style={{marginBottom: "12px"}}>
							<strong>Position Offsets:</strong>
							<div style={{display: "flex", alignItems: "center", gap: "12px", marginTop: "8px"}}>
								<label style={{width: "16px"}}>X</label>
								<input type="range" min="-1" max="1" step="0.01" value={positionOffsetX} onChange={e => setPositionOffsetX(parseFloat(e.target.value))} style={{flex: 1}} />
								<span style={{background: "#343a40", color: "white", padding: "2px 8px", borderRadius: "4px"}}>{positionOffsetX.toFixed(2)}</span>
							</div>
							<div style={{display: "flex", alignItems: "center", gap: "12px", marginTop: "8px"}}>
								<label style={{width: "16px"}}>Y</label>
								<input type="range" min="-1" max="1" step="0.01" value={positionOffsetY} onChange={e => setPositionOffsetY(parseFloat(e.target.value))} style={{flex: 1}} />
								<span style={{background: "#343a40", color: "white", padding: "2px 8px", borderRadius: "4px"}}>{positionOffsetY.toFixed(2)}</span>
							</div>
							<div style={{display: "flex", alignItems: "center", gap: "12px", marginTop: "8px"}}>
								<label style={{width: "16px"}}>Z</label>
								<input type="range" min="-1" max="1" step="0.01" value={positionOffsetZ} onChange={e => setPositionOffsetZ(parseFloat(e.target.value))} style={{flex: 1}} />
								<span style={{background: "#343a40", color: "white", padding: "2px 8px", borderRadius: "4px"}}>{positionOffsetZ.toFixed(2)}</span>
							</div>
							<div style={{marginTop: "8px"}}>
								<button onClick={resetPositionOffsets} style={{background: "#6c757d", color: "white", border: "none", padding: "6px 12px", borderRadius: "4px", cursor: "pointer"}}>Reset Position</button>
							</div>
						</div>
						<div>
							<strong>Axis Scale Multipliers:</strong>
							<div style={{display: "flex", alignItems: "center", gap: "12px", marginTop: "8px"}}>
								<label style={{width: "16px"}}>X</label>
								<input type="range" min="0.5" max="2" step="0.1" value={scaleXMultiplier} onChange={e => setScaleXMultiplier(parseFloat(e.target.value))} style={{flex: 1}} />
								<span style={{background: "#343a40", color: "white", padding: "2px 8px", borderRadius: "4px"}}>{scaleXMultiplier.toFixed(1)}x</span>
							</div>
							<div style={{display: "flex", alignItems: "center", gap: "12px", marginTop: "8px"}}>
								<label style={{width: "16px"}}>Y</label>
								<input type="range" min="0.5" max="2" step="0.1" value={scaleYMultiplier} onChange={e => setScaleYMultiplier(parseFloat(e.target.value))} style={{flex: 1}} />
								<span style={{background: "#343a40", color: "white", padding: "2px 8px", borderRadius: "4px"}}>{scaleYMultiplier.toFixed(1)}x</span>
							</div>
							<div style={{display: "flex", alignItems: "center", gap: "12px", marginTop: "8px"}}>
								<label style={{width: "16px"}}>Z</label>
								<input type="range" min="0.5" max="2" step="0.1" value={scaleZMultiplier} onChange={e => setScaleZMultiplier(parseFloat(e.target.value))} style={{flex: 1}} />
								<span style={{background: "#343a40", color: "white", padding: "2px 8px", borderRadius: "4px"}}>{scaleZMultiplier.toFixed(1)}x</span>
							</div>
							<div style={{marginTop: "8px"}}>
								<button onClick={resetAxisScaleMultipliers} style={{background: "#6c757d", color: "white", border: "none", padding: "6px 12px", borderRadius: "4px", cursor: "pointer"}}>Reset Axis Scales</button>
							</div>
						</div>
					</div>
				</div>

			{/* Status Panel */}
			<div
				style={{
					background: "#f8f9fa",
					padding: "15px",
					borderRadius: "8px",
					marginBottom: "20px",
				}}
			>
				<h4>Tracking Status:</h4>
				<div
					style={{
						display: "grid",
						gridTemplateColumns:
							"repeat(auto-fit, minmax(200px, 1fr))",
						gap: "10px",
					}}
				>
					<div>
						<strong>3D Model:</strong>{" "}
						{modelLoaded ? "✅ Loaded" : "❌ Missing"}
					</div>
					<div>
						<strong>Face Tracking:</strong>{" "}
						{faceDetected ? "✅ Active" : "❌ Searching"}
					</div>
					<div>
						<strong>Face Size Detection:</strong>{" "}
						{faceDetected ? "✅ Measuring" : "❌ Off"}
					</div>
					<div>
						<strong>Auto-Scaling:</strong>{" "}
						{faceDetected ? "✅ Active" : "❌ Off"}
					</div>
				</div>
			</div>

			{/* Debug Info */}
			<div
				style={{
					background: "#f8f9fa",
					padding: "15px",
					borderRadius: "8px",
				}}
			>
				<h4>Debug Log:</h4>
				<div
					style={{
						height: "120px",
						overflowY: "auto",
						background: "white",
						border: "1px solid #ddd",
						borderRadius: "4px",
						padding: "10px",
						fontSize: "12px",
						fontFamily: "monospace",
					}}
				>
					{debugInfo.length === 0 ? (
						<div style={{color: "#666"}}>
							System ready. Load model and face camera.
						</div>
					) : (
						debugInfo.map((info, index) => (
							<div
								key={index}
								style={{marginBottom: "2px"}}
							>
								[{info.timestamp}] {info.message}
							</div>
						))
					)}
				</div>
			</div>
		</div>
	);
};

export default SimpleVTO;
